import { supabase } from '../lib/supabaseClient';
import { PgApiError, traerTodo } from './pg';
import type {
  CierreYiMercado, FichaYiBodega, FichaYiMercado, FichaYiModerno, InstrumentoYi, Json,
} from '../domain/yichang/tipos';

/**
 * La lectura del levantamiento de **Yichang** (Pringles y Kellogg's): canal moderno, bodegas y
 * mercados, más el cierre de jornada de mercados. Migración 0242.
 *
 * ⚠️ **No es el de P&G** (`api/pg.ts`) ni el de Softys (`api/levantamiento.ts`). Los tres se llaman
 * «levantamiento»; este lee SOLO tablas `yi_*`.
 *
 * ## Quién puede leer esto
 *
 * Las cuatro tablas tienen RLS de `has_role('admin')` o `has_role('datos')`: a cualquier otro rol la
 * consulta le devuelve cero filas **sin error**. Por eso la pantalla es la misma que la de P&G, con
 * el mismo permiso (`levantamientoPg`), que solo ven esos dos roles.
 *
 * ⚠️ `yi_contactos` (el celular del bodeguero, dato de un tercero) **no se lee acá**: solo la ve el
 * admin y no tiene nada que hacer en una bandeja de revisión. De la bodega se trae solo si aceptó
 * darlo (`contacto_consentido`).
 *
 * La paginación y el control de «llegaron N de M» son los de P&G (`traerTodo`): una lectura corta en
 * silencio produce un resumen completo, coherente y equivocado.
 */

const texto = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const numero = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const lista = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const ficha = (v: unknown): Json => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Json) : {});

function cabecera(r: Record<string, unknown>) {
  return {
    id: String(r.id),
    supervisor: texto(r.supervisor),
    fecha: String(r.fecha),
    ocurridoEn: texto(r.ocurrido_en),
    registradoEn: texto(r.registrado_en),
    lat: numero(r.lat),
    lng: numero(r.lng),
    accuracy: numero(r.accuracy),
    ficha: ficha(r.ficha),
    fotos: lista(r.fotos),
    flags: lista(r.flags),
    origin: texto(r.origin),
    duracionSeg: numero(r.duracion_seg),
  };
}

const COMUNES = 'id, supervisor, fecha, ocurrido_en, registrado_en, lat, lng, accuracy, ficha, fotos, flags, '
  + 'origin, duracion_seg';

/**
 * Se ordena por fecha y después por hora: `ocurrido_en` puede venir en NULL, y ordenando solo por él
 * esas fichas se irían al fondo aunque sean del día que se está mirando.
 *
 * El `id` al final no es de adorno: `traerTodo` pagina de a mil, y con un orden que empata (dos fichas
 * con la misma fecha y hora, o las dos sin hora) Postgres no garantiza el mismo orden entre páginas.
 * Una fila podría salir dos veces y otra ninguna, y el control de «llegaron N de M» no lo vería,
 * porque solo compara cuántas.
 */
type Consulta = Parameters<Parameters<typeof traerTodo>[2]>[0];
const enRango = (desde: string, hasta: string) => (q: Consulta) => q.gte('fecha', desde).lte('fecha', hasta)
  .order('fecha', { ascending: false })
  .order('ocurrido_en', { ascending: false, nullsFirst: false })
  .order('id', { ascending: true });

export async function listFichasYiModerno(desde: string, hasta: string): Promise<FichaYiModerno[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'yi_fichas_moderno', `${COMUNES}, tienda_id, pdv_id, tienda_texto, cadena, formato`, enRango(desde, hasta),
  );
  return crudas.map((r) => ({
    ...cabecera(r),
    tiendaId: texto(r.tienda_id),
    pdvId: texto(r.pdv_id),
    tiendaTexto: texto(r.tienda_texto),
    cadena: texto(r.cadena),
    formato: texto(r.formato),
  }));
}

export async function listFichasYiBodega(desde: string, hasta: string): Promise<FichaYiBodega[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'yi_fichas_bodega',
    `${COMUNES}, tienda_nombre, tienda_direccion, tipo_tienda, modo_visita, zona, distrito, `
    + 'es_cliente_yichang, codigo_cliente_yichang, contacto_consentido',
    enRango(desde, hasta),
  );
  return crudas.map((r) => ({
    ...cabecera(r),
    tiendaNombre: texto(r.tienda_nombre),
    tiendaDireccion: texto(r.tienda_direccion),
    tipoTienda: texto(r.tipo_tienda),
    modoVisita: texto(r.modo_visita),
    zona: texto(r.zona),
    distrito: texto(r.distrito),
    esClienteYichang: texto(r.es_cliente_yichang),
    codigoClienteYichang: texto(r.codigo_cliente_yichang),
    contactoConsentido: typeof r.contacto_consentido === 'boolean' ? r.contacto_consentido : null,
  }));
}

export async function listFichasYiMercado(desde: string, hasta: string): Promise<FichaYiMercado[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'yi_fichas_mercado',
    `${COMUNES}, mercado_nombre, mercado_direccion, zona, distrito, puesto_numero, puesto_nombre, levantado, motivo_no`,
    enRango(desde, hasta),
  );
  return crudas.map((r) => ({
    ...cabecera(r),
    mercadoNombre: texto(r.mercado_nombre),
    mercadoDireccion: texto(r.mercado_direccion),
    zona: texto(r.zona),
    distrito: texto(r.distrito),
    puestoNumero: numero(r.puesto_numero),
    puestoNombre: texto(r.puesto_nombre),
    // Sin valor se trata como NO levantado: contar un puesto como medido porque falta el dato infla
    // la cobertura, que es el número que decide si la ruta se repite.
    levantado: r.levantado === true,
    motivoNo: texto(r.motivo_no),
  }));
}

export async function listCierresYiMercado(desde: string, hasta: string): Promise<CierreYiMercado[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'yi_cierres_mercado',
    'id, supervisor, mercado_nombre, zona, distrito, fecha, ficha, observacion, ocurrido_en, registrado_en, origin',
    enRango(desde, hasta),
  );
  return crudas.map((r) => ({
    id: String(r.id),
    supervisor: texto(r.supervisor),
    mercadoNombre: texto(r.mercado_nombre),
    zona: texto(r.zona),
    distrito: texto(r.distrito),
    fecha: String(r.fecha),
    ficha: ficha(r.ficha),
    observacion: texto(r.observacion),
    ocurridoEn: texto(r.ocurrido_en),
    registradoEn: texto(r.registrado_en),
    origin: texto(r.origin),
  }));
}

export const TABLA_DE: Record<InstrumentoYi, string> = {
  moderno: 'yi_fichas_moderno',
  bodegas: 'yi_fichas_bodega',
  mercados: 'yi_fichas_mercado',
};

/**
 * La fecha más vieja y la más nueva del instrumento, **sin filtro de rango**, y cuántas fichas hay.
 *
 * Es lo que deja abrir la pantalla en el rango completo del estudio y decir «hay N fichas fuera del
 * rango»: un total honesto dentro de un rango que nadie eligió es la forma más fácil de esconder
 * media muestra (la lección de las 4 fichas de Makro del 29-ago en P&G).
 */
export async function bordesYichang(
  instrumento: InstrumentoYi,
): Promise<{ primera: string | null; ultima: string | null; total: number }> {
  const tabla = TABLA_DE[instrumento];
  const { data, error, count } = await supabase
    .from(tabla).select('fecha', { count: 'exact' })
    .order('fecha', { ascending: true }).limit(1);
  if (error) throw new PgApiError(error.message, error.code ?? null);
  // `nullsFirst: false`: en orden descendente Postgres pone los NULL PRIMERO, y una sola ficha sin
  // fecha haría decir «el estudio va del 1 al —». Las que no tienen fecha igual cuentan en `total`, y
  // por eso aparecen como «fuera del rango»: no se esconden.
  const { data: d2, error: e2 } = await supabase
    .from(tabla).select('fecha').order('fecha', { ascending: false, nullsFirst: false }).limit(1);
  if (e2) throw new PgApiError(e2.message, e2.code ?? null);
  return {
    primera: (data?.[0]?.fecha as string | undefined) ?? null,
    ultima: (d2?.[0]?.fecha as string | undefined) ?? null,
    total: count ?? 0,
  };
}
